/**
 * API client for payments export endpoints
 */

import { apiClient } from '../../../shared/api/axiosClient';

export interface PaymentRowDto {
  personName: string;
  potReference: string;
  accountNumber: string | null;
  sortCode: string | null;
  companyName: string | null;
  amountDue: number;
  potStatus: string;
}

export interface GetPaymentsResponse {
  payments: PaymentRowDto[];
  periodStart: string;
  periodEnd: string;
}

/**
 * Fetches payment data for approved pots in a period
 * @param potPeriodId - The pot period to get payments for
 * @returns Promise resolving to payment rows and period dates
 */
export async function getPayments(
  potPeriodId: string
): Promise<GetPaymentsResponse> {
  const response = await apiClient.get<GetPaymentsResponse>(
    `/api/v1/pots/payments?potPeriodId=${potPeriodId}`
  );
  return response.data;
}

/**
 * Returns the URL for downloading payments as CSV
 * The URL includes auth token via the axiosClient interceptor
 * @param potPeriodId - The pot period to export
 * @returns The export URL
 */
export function getPaymentsCsvUrl(potPeriodId: string): string {
  return `/api/v1/pots/payments/export?potPeriodId=${potPeriodId}`;
}

/**
 * Downloads payments as CSV file
 * Uses fetch with auth token from axiosClient
 * @param potPeriodId - The pot period to export
 */
export async function downloadPaymentsCsv(potPeriodId: string): Promise<void> {
  const response = await apiClient.get<Blob>(
    `/api/v1/pots/payments/export?potPeriodId=${potPeriodId}`,
    {
      responseType: 'blob',
    }
  );

  // Get filename from Content-Disposition header or use default
  const contentDisposition = response.headers['content-disposition'];
  let filename = 'payments.csv';
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
    if (filenameMatch) {
      filename = filenameMatch[1];
    }
  }

  // Create download link
  const url = window.URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
